import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import PlatformStatsCampaignCodePreview, {
  PreviewDraft,
} from './PlatformStatsCampaignCodePreview';
import './platform-stats-campaign-codes.scss';
import {
  CHANNELS,
  ChannelId,
  channelOf,
  channelOfCode,
} from './acquisitionFunnel';

const channelLabel = (id: ChannelId) =>
  CHANNELS.find((c) => c.id === id)?.label ?? id;

type Validity = 'ok' | 'inactive' | 'not_started' | 'expired' | 'exhausted';

export interface CampaignCode {
  id: string;
  code: string;
  fullCode: string;
  whatsappUrl: string;
  campaignName: string;
  source: string;
  /** Reporting channel on Platform Stats; null = derived from source. */
  channel: string | null;
  publicLabel: string;
  welcomeCredits: number;
  welcomeMessageGuidance: string | null;
  notes: string | null;
  active: boolean;
  startsAt: string | null;
  expiresAt: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  provisionedCount: number;
  creditedCount: number;
  validity: Validity;
  createdByUserId: string | null;
  deactivatedAt: string | null;
  createdAt: string;
}

interface ListResponse {
  success: boolean;
  codes: CampaignCode[];
}

interface CodeResponse {
  success: boolean;
  code: CampaignCode;
}

const VALIDITY_LABELS: Record<Validity, string> = {
  ok: 'Live',
  inactive: 'Inactive',
  not_started: 'Scheduled',
  expired: 'Expired',
  exhausted: 'Cap reached',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/** ISO → value for <input type="datetime-local"> in the browser's local time. */
const toLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

/** datetime-local value → ISO, or null when blank. */
const fromLocalInput = (value: string): string | null => {
  if (!value.trim()) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as { response?: { data?: { error?: string } } };
  return axiosErr?.response?.data?.error || fallback;
};

const CopyButton: FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — user can still select the text
    }
  };
  return (
    <button type='button' className='copy-btn' onClick={copy}>
      {copied ? 'Copied' : label}
    </button>
  );
};

interface CodeFormState {
  code: string;
  campaignName: string;
  source: string;
  /** '' = default from source. */
  channel: string;
  publicLabel: string;
  welcomeCredits: string;
  welcomeMessageGuidance: string;
  notes: string;
  startsAt: string;
  expiresAt: string;
  maxRedemptions: string;
}

const EMPTY_FORM: CodeFormState = {
  code: '',
  campaignName: '',
  source: '',
  channel: '',
  publicLabel: '',
  welcomeCredits: '',
  welcomeMessageGuidance: '',
  notes: '',
  startsAt: '',
  expiresAt: '',
  maxRedemptions: '',
};

const formFromCode = (c: CampaignCode): CodeFormState => ({
  code: c.code,
  campaignName: c.campaignName,
  source: c.source,
  channel: c.channel ?? '',
  publicLabel: c.publicLabel,
  welcomeCredits: String(c.welcomeCredits),
  welcomeMessageGuidance: c.welcomeMessageGuidance ?? '',
  notes: c.notes ?? '',
  startsAt: toLocalInput(c.startsAt),
  expiresAt: toLocalInput(c.expiresAt),
  maxRedemptions: c.maxRedemptions == null ? '' : String(c.maxRedemptions),
});

const formToPayload = (f: CodeFormState) => ({
  campaignName: f.campaignName.trim(),
  source: f.source.trim(),
  channel: f.channel || null,
  publicLabel: f.publicLabel.trim(),
  welcomeCredits: Number(f.welcomeCredits || 0),
  welcomeMessageGuidance: f.welcomeMessageGuidance.trim() || null,
  notes: f.notes.trim() || null,
  startsAt: fromLocalInput(f.startsAt),
  expiresAt: fromLocalInput(f.expiresAt),
  maxRedemptions: f.maxRedemptions.trim() ? Number(f.maxRedemptions) : null,
});

/** The unsaved form values as the preview endpoint wants them. */
const formToPreviewDraft = (
  f: CodeFormState,
  active: boolean,
): PreviewDraft => ({
  code: f.code.trim() || null,
  publicLabel: f.publicLabel.trim(),
  welcomeCredits: Number(f.welcomeCredits || 0),
  welcomeMessageGuidance: f.welcomeMessageGuidance.trim() || null,
  active,
  startsAt: fromLocalInput(f.startsAt),
  expiresAt: fromLocalInput(f.expiresAt),
  maxRedemptions: f.maxRedemptions.trim() ? Number(f.maxRedemptions) : null,
});

const isWholeNumber = (raw: string, min: number): boolean => {
  const n = Number(raw);
  return raw.trim().length > 0 && Number.isInteger(n) && n >= min;
};

/** Shown under the field as soon as something invalid is typed. */
const welcomeCreditsError = (raw: string): string | null =>
  raw.trim().length === 0 || isWholeNumber(raw, 0)
    ? null
    : 'Welcome credits must be a whole number of 0 or more.';

const maxRedemptionsError = (raw: string): string | null =>
  raw.trim().length === 0 || isWholeNumber(raw, 1)
    ? null
    : 'Max redemptions must be a whole number of 1 or more (leave blank for unlimited).';

const formValid = (f: CodeFormState): boolean =>
  f.campaignName.trim().length > 0 &&
  f.source.trim().length > 0 &&
  f.publicLabel.trim().length > 0 &&
  isWholeNumber(f.welcomeCredits, 0) &&
  maxRedemptionsError(f.maxRedemptions) === null;

/** Only the fields the greeting depends on need to be filled in to preview. */
const previewDisabledReason = (f: CodeFormState): string | null =>
  f.publicLabel.trim().length === 0 || !isWholeNumber(f.welcomeCredits, 0)
    ? 'Fill in the public label and welcome credits to preview the greeting.'
    : null;

interface FormPreviewProps {
  idPrefix: string;
  form: CodeFormState;
  active: boolean;
}

/** Collapsible "Preview greeting" section under the create / edit form. */
const FormPreview: FC<FormPreviewProps> = ({ idPrefix, form, active }) => {
  const [open, setOpen] = useState(false);
  const reason = previewDisabledReason(form);
  return (
    <div className='form-preview'>
      <button
        type='button'
        className='ghost-btn'
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'Hide greeting preview' : 'Preview greeting'}
      </button>
      {open && (
        <PlatformStatsCampaignCodePreview
          idPrefix={idPrefix}
          target={{ draft: formToPreviewDraft(form, active) }}
          disabled={reason !== null}
          disabledReason={reason ?? undefined}
        />
      )}
    </div>
  );
};

interface CodeFieldsProps {
  idPrefix: string;
  form: CodeFormState;
  onChange: (patch: Partial<CodeFormState>) => void;
  editing: boolean;
}

const CodeFields: FC<CodeFieldsProps> = ({
  idPrefix,
  form,
  onChange,
  editing,
}) => (
  <>
    <div className='field'>
      <label htmlFor={`${idPrefix}-code`}>Code</label>
      <input
        id={`${idPrefix}-code`}
        type='text'
        value={form.code}
        onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
        placeholder='Leave blank to generate'
        autoComplete='off'
        disabled={editing}
        spellCheck={false}
      />
      <span className='field-hint'>
        {editing
          ? 'Codes cannot be changed once created — deactivate and create a new one instead.'
          : 'Letters and digits, 4–32 characters (not exactly 8 — that length is reserved for user invite codes). Users send it as DREAM-<code>.'}
      </span>
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-campaignName`}>Campaign name</label>
      <input
        id={`${idPrefix}-campaignName`}
        type='text'
        value={form.campaignName}
        onChange={(e) => onChange({ campaignName: e.target.value })}
        placeholder='British Airways spring 2026'
        required
        autoComplete='off'
      />
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-source`}>Source</label>
      <input
        id={`${idPrefix}-source`}
        type='text'
        value={form.source}
        onChange={(e) => onChange({ source: e.target.value })}
        placeholder='british_airways'
        required
        autoComplete='off'
      />
      <span className='field-hint'>
        Attribution key for reporting (platform, partner, channel…).
      </span>
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-channel`}>Channel</label>
      <select
        id={`${idPrefix}-channel`}
        value={form.channel}
        onChange={(e) => onChange({ channel: e.target.value })}
      >
        <option value=''>
          Default from source
          {form.source.trim()
            ? ` (${channelLabel(channelOf(`campaign:${form.source.trim()}`))})`
            : ''}
        </option>
        {CHANNELS.map((ch) => (
          <option key={ch.id} value={ch.id}>
            {ch.label}
          </option>
        ))}
      </select>
      <span className='field-hint'>
        Where this code counts on Acquisition and Growth. Codes sharing a source
        can sit in different channels.
      </span>
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-publicLabel`}>Public label</label>
      <input
        id={`${idPrefix}-publicLabel`}
        type='text'
        value={form.publicLabel}
        onChange={(e) => onChange({ publicLabel: e.target.value })}
        placeholder='British Airways'
        required
        autoComplete='off'
      />
      <span className='field-hint'>
        How the secretary refers to the campaign in its first message, e.g.
        &ldquo;Welcome from our British Airways promotion&rdquo;.
      </span>
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-welcomeCredits`}>Welcome credits</label>
      <input
        id={`${idPrefix}-welcomeCredits`}
        type='number'
        min={0}
        step={1}
        value={form.welcomeCredits}
        onChange={(e) => onChange({ welcomeCredits: e.target.value })}
        placeholder='2000'
        required
        aria-invalid={welcomeCreditsError(form.welcomeCredits) !== null}
      />
      {welcomeCreditsError(form.welcomeCredits) ? (
        <span className='field-error' role='alert'>
          {welcomeCreditsError(form.welcomeCredits)}
        </span>
      ) : (
        <span className='field-hint'>
          Granted once the user finishes setup (same milestone as referral
          rewards). 0 = attribution only.
        </span>
      )}
    </div>
    <div className='field'>
      <label htmlFor={`${idPrefix}-welcomeMessageGuidance`}>
        First-message guidance
      </label>
      <textarea
        id={`${idPrefix}-welcomeMessageGuidance`}
        value={form.welcomeMessageGuidance}
        onChange={(e) => onChange({ welcomeMessageGuidance: e.target.value })}
        placeholder='Mention the offer comes with their Executive Club membership; the secretary is great at travel plans.'
        rows={3}
      />
      <span className='field-hint'>
        Optional. Guidance, not verbatim copy — the secretary phrases it in the
        user&apos;s language.
      </span>
    </div>
    <div className='field-row'>
      <div className='field'>
        <label htmlFor={`${idPrefix}-startsAt`}>Starts</label>
        <input
          id={`${idPrefix}-startsAt`}
          type='datetime-local'
          value={form.startsAt}
          onChange={(e) => onChange({ startsAt: e.target.value })}
        />
      </div>
      <div className='field'>
        <label htmlFor={`${idPrefix}-expiresAt`}>Expires</label>
        <input
          id={`${idPrefix}-expiresAt`}
          type='datetime-local'
          value={form.expiresAt}
          onChange={(e) => onChange({ expiresAt: e.target.value })}
        />
      </div>
      <div className='field'>
        <label htmlFor={`${idPrefix}-maxRedemptions`}>Max redemptions</label>
        <input
          id={`${idPrefix}-maxRedemptions`}
          type='number'
          min={1}
          step={1}
          value={form.maxRedemptions}
          onChange={(e) => onChange({ maxRedemptions: e.target.value })}
          placeholder='Unlimited'
          aria-invalid={maxRedemptionsError(form.maxRedemptions) !== null}
        />
      </div>
    </div>
    {maxRedemptionsError(form.maxRedemptions) && (
      <span className='field-error' role='alert'>
        {maxRedemptionsError(form.maxRedemptions)}
      </span>
    )}
    <div className='field'>
      <label htmlFor={`${idPrefix}-notes`}>Internal notes</label>
      <textarea
        id={`${idPrefix}-notes`}
        value={form.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder='Budget, owner, landing page…'
        rows={2}
      />
    </div>
  </>
);

/**
 * Admin "Campaign Codes" tab: marketing acquisition codes that ads deep-link
 * into WhatsApp with (DREAM-<code>). Lets a platform-stats admin create a
 * code (custom or generated), set the welcome credits and first-message
 * guidance, schedule/cap it, edit it, and deactivate it. Codes are never
 * deleted — redemptions are attribution history.
 */
const PlatformStatsCampaignCodes: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [codes, setCodes] = useState<CampaignCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [form, setForm] = useState<CodeFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<CampaignCode | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CodeFormState>(EMPTY_FORM);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{
    id: string;
    message: string;
  } | null>(null);

  const authHeaders = useCallback(
    async () => ({
      headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
    }),
    [getAccessTokenSilently],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await api.get<ListResponse>(
        '/platform-stats/acquisition-codes',
        await authHeaders(),
      );
      if (res.data.success) setCodes(res.data.codes);
    } catch (err) {
      setListError(errorMessage(err, 'Failed to load campaign codes'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || !formValid(form)) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await api.post<CodeResponse>(
        '/platform-stats/acquisition-codes',
        { ...formToPayload(form), code: form.code.trim() || null },
        await authHeaders(),
      );
      setCreated(res.data.code);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setFormError(errorMessage(err, 'Failed to create campaign code'));
    } finally {
      setSubmitting(false);
    }
  };

  const patchCode = async (
    c: CampaignCode,
    body: Record<string, unknown>,
    successNotice: string,
  ): Promise<boolean> => {
    if (busyId) return false;
    setBusyId(c.id);
    setRowError(null);
    setNotice(null);
    try {
      await api.patch(
        `/platform-stats/acquisition-codes/${encodeURIComponent(c.id)}`,
        body,
        await authHeaders(),
      );
      setNotice(successNotice);
      await load();
      return true;
    } catch (err) {
      setRowError({
        id: c.id,
        message: errorMessage(err, 'Failed to update campaign code'),
      });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (c: CampaignCode) =>
    patchCode(
      c,
      { active: !c.active },
      c.active
        ? `${c.fullCode} deactivated — new signups with this code are no longer accepted.`
        : `${c.fullCode} reactivated.`,
    );

  const startEdit = (c: CampaignCode) => {
    setRowError(null);
    setEditingId(c.id);
    setEditForm(formFromCode(c));
  };

  const saveEdit = async (c: CampaignCode) => {
    if (!formValid(editForm)) return;
    const ok = await patchCode(
      c,
      formToPayload(editForm),
      `${c.fullCode} updated.`,
    );
    if (ok) setEditingId(null);
  };

  const liveCount = codes.filter((c) => c.validity === 'ok').length;

  return (
    <div className='campaign-codes'>
      <div className='campaign-codes-grid'>
        <section className='campaign-codes-card create-card'>
          <h3>New campaign code</h3>
          <p className='card-hint'>
            Put the campaign link behind an ad. New users see the Dream
            invitation page, then open WhatsApp with
            <code> DREAM-&lt;code&gt;</code> prefilled. They are attributed to
            this campaign (not to a referrer) and earn the welcome credits once
            they finish setup.
          </p>

          {created ? (
            <div className='created-panel' role='status'>
              <h4>{created.campaignName} is ready</h4>
              <dl>
                <dt>Code</dt>
                <dd>
                  <code className='code'>{created.fullCode}</code>
                  <CopyButton value={created.fullCode} label='Copy' />
                </dd>
                <dt>Campaign link</dt>
                <dd>
                  <a
                    href={created.whatsappUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {created.whatsappUrl}
                  </a>
                  <CopyButton value={created.whatsappUrl} label='Copy' />
                </dd>
                <dt>Credits</dt>
                <dd>{created.welcomeCredits.toLocaleString('en-GB')}</dd>
              </dl>
              <button
                type='button'
                className='primary-btn'
                onClick={() => setCreated(null)}
              >
                Create another
              </button>
            </div>
          ) : (
            <form className='create-form' onSubmit={submit}>
              <CodeFields
                idPrefix='new'
                form={form}
                editing={false}
                onChange={(patch) => {
                  setFormError(null);
                  setForm((f) => ({ ...f, ...patch }));
                }}
              />
              {formError && (
                <div className='form-error' role='alert'>
                  {formError}
                </div>
              )}
              <button
                type='submit'
                className='primary-btn'
                disabled={submitting || !formValid(form)}
              >
                {submitting ? 'Creating…' : 'Create campaign code'}
              </button>
            </form>
          )}
          {!created && <FormPreview idPrefix='new' form={form} active />}
        </section>

        <section className='campaign-codes-card list-card'>
          <div className='list-header'>
            <h3>Campaign codes</h3>
            <span className='list-count'>
              {loading
                ? 'Loading…'
                : `${codes.length} codes · ${liveCount} live`}
            </span>
          </div>
          {listError && (
            <div className='form-error' role='alert'>
              {listError}
            </div>
          )}
          {notice && (
            <div className='list-notice' role='status'>
              {notice}
            </div>
          )}
          <div className='campaign-codes-table-wrap'>
            <table className='campaign-codes-table'>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Credits</th>
                  <th>Redemptions</th>
                  <th>Window</th>
                  <th>Created</th>
                  <th className='actions-col'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && codes.length === 0 && (
                  <tr>
                    <td className='empty' colSpan={8}>
                      No campaign codes yet
                    </td>
                  </tr>
                )}
                {codes.map((c) => {
                  const busy = busyId === c.id;
                  const editing = editingId === c.id;
                  const previewing = previewId === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <tr className={editing ? 'editing' : undefined}>
                        <td>
                          <div className='code-cell'>
                            <code>{c.fullCode}</code>
                            <span className='code-links'>
                              <CopyButton
                                value={c.fullCode}
                                label='Copy code'
                              />
                              <CopyButton
                                value={c.whatsappUrl}
                                label='Copy link'
                              />
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className='campaign-cell'>
                            <span>{c.campaignName}</span>
                            <span className='muted'>
                              {c.source} · &ldquo;{c.publicLabel}&rdquo;
                            </span>
                            <span className='muted'>
                              {channelLabel(
                                channelOfCode(
                                  `campaign:${c.source}`,
                                  c.channel,
                                ),
                              )}
                              {c.channel ? '' : ' (from source)'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-chip ${c.validity}`}>
                            {VALIDITY_LABELS[c.validity]}
                          </span>
                        </td>
                        <td>{c.welcomeCredits.toLocaleString('en-GB')}</td>
                        <td>
                          <div className='campaign-cell'>
                            <span>
                              {c.redemptionCount.toLocaleString('en-GB')}
                              {c.maxRedemptions != null && (
                                <span className='muted'>
                                  {' '}
                                  / {c.maxRedemptions.toLocaleString('en-GB')}
                                </span>
                              )}
                            </span>
                            <span className='muted'>
                              {c.provisionedCount} signed up · {c.creditedCount}{' '}
                              credited
                            </span>
                          </div>
                        </td>
                        <td>
                          {c.startsAt || c.expiresAt
                            ? `${formatDate(c.startsAt)} → ${formatDate(c.expiresAt)}`
                            : 'No expiry'}
                        </td>
                        <td>{formatDate(c.createdAt)}</td>
                        <td className='actions-col'>
                          <div className='row-actions'>
                            <button
                              type='button'
                              className='ghost-btn'
                              aria-expanded={previewing}
                              title='Simulate how a new user is greeted with this code'
                              onClick={() =>
                                setPreviewId(previewing ? null : c.id)
                              }
                            >
                              {previewing ? 'Hide preview' : 'Preview'}
                            </button>
                            <button
                              type='button'
                              className='ghost-btn'
                              disabled={busyId !== null}
                              onClick={() =>
                                editing ? setEditingId(null) : startEdit(c)
                              }
                            >
                              {editing ? 'Cancel' : 'Edit'}
                            </button>
                            <button
                              type='button'
                              className={`ghost-btn ${c.active ? 'danger' : ''}`}
                              disabled={busyId !== null}
                              title={
                                c.active
                                  ? 'Stop accepting this code (history is kept)'
                                  : 'Accept this code again'
                              }
                              onClick={() => toggleActive(c)}
                            >
                              {busy
                                ? 'Saving…'
                                : c.active
                                  ? 'Deactivate'
                                  : 'Reactivate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editing && (
                        <tr className='edit-row'>
                          <td colSpan={8}>
                            <form
                              className='create-form edit-form'
                              onSubmit={(e) => {
                                e.preventDefault();
                                saveEdit(c);
                              }}
                            >
                              <CodeFields
                                idPrefix={`edit-${c.id}`}
                                form={editForm}
                                editing
                                onChange={(patch) =>
                                  setEditForm((f) => ({ ...f, ...patch }))
                                }
                              />
                              <div className='edit-actions'>
                                <button
                                  type='submit'
                                  className='primary-btn'
                                  disabled={busy || !formValid(editForm)}
                                >
                                  {busy ? 'Saving…' : 'Save changes'}
                                </button>
                                <button
                                  type='button'
                                  className='ghost-btn'
                                  disabled={busy}
                                  onClick={() => setEditingId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                            <FormPreview
                              idPrefix={`edit-${c.id}`}
                              form={editForm}
                              active={c.active}
                            />
                          </td>
                        </tr>
                      )}
                      {previewing && (
                        <tr className='preview-row'>
                          <td colSpan={8}>
                            <div className='row-preview'>
                              <h4>
                                Greeting preview for <code>{c.fullCode}</code>
                              </h4>
                              <PlatformStatsCampaignCodePreview
                                idPrefix={`row-${c.id}`}
                                target={{ codeId: c.id }}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                      {rowError?.id === c.id && (
                        <tr className='row-error-row'>
                          <td colSpan={8}>
                            <div className='form-error' role='alert'>
                              {rowError.message}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlatformStatsCampaignCodes;
